
import { Deserializer } from "jsonapi-serializer";
import { INTERNAL_SERVER_ERROR, NO_CONTENT } from "../constants/response-status-codes";
import { AccessGroupAction, AccessGroupActionWrapper } from "../models/AccessGroupAction";
import { AccessGroupDeviceAction, AccessGroupDeviceActionWrapper } from "../models/AccessGroupDeviceAction";
import { genericError } from "../models/GenericError";
import { ListQuery } from "../models/ListQuery";
import { StoreEntityResult } from "../models/services/StoreEntityResult";
import { APIConnector } from "./api/APIConnector";
import { processServerError, getConstraintViolation } from "./utilities/ServiceError";
import { createPagination } from "./utilities/paginationBuilder";
import { createPartialURL, getFilterClause } from "./utilities/urlBuilder";
import { Name } from "../models/Name";
import { PaginationModel } from "../models/Pagination";

const apiConnector = new APIConnector();

export const getAccessGroupActions = async (id: string, agaValue: string, listParameters?: ListQuery): Promise<AccessGroupActionWrapper> => {
    //agaDirection 0 ~ entry
    //agaDirection 1 ~ exit
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const withAdditionalFilter = `${filter ? filter.replace('filter=', `filter=accessGroup.id==${id};agaDirection==${agaValue};`) : `&filter=accessGroup.id==${id};agaDirection==${agaValue}`}`;
        const paginationString = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, withAdditionalFilter);
        const agaFields = 'fields[accessGroupAction]=devAction';
        const devActionFields = 'fields[devAction]=setting,deviceImprox';
        const settingFields = 'fields[setting]=settingType,trnName';
        const settingTypeFields = 'fields[settingType]=trnName';
        const deviceImproxFields = 'fields[deviceImprox]=devName';
        const url = `accessGroupAction?include=accessGroup,devAction.setting.settingType,devAction.deviceImprox${paginationString}&${agaFields}&${devActionFields}&${settingFields}&${settingTypeFields}&${deviceImproxFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({status: response.status, message: genericError });
                }

                const accessGroupAction: AccessGroupAction[] = data.map((accessGroupAction): AccessGroupAction => {
                    return {
                        id: accessGroupAction['id'],
                        name: accessGroupAction['dev-action']['setting']['trn-name'],
                        type: accessGroupAction['dev-action']['setting']['setting-type']['trn-name'],
                        deviceId: accessGroupAction['dev-action']['id'],
                        device: accessGroupAction['dev-action']['device-improx']['dev-name']

                    }
                })
                return resolve({ rows: accessGroupAction, pagination });
            })
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getDeviceActionsForAccessGroups = async (listParameters?: ListQuery): Promise<AccessGroupDeviceActionWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const paginationString = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const url = `devAction?include=deviceImprox,setting.settingType${paginationString}`
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);
        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }
            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({status: response.status, message: genericError });
                }

                const deviceAction: AccessGroupDeviceAction[] = data.map((deviceAction): AccessGroupDeviceAction => {
                    return {
                        id: deviceAction['id'],
                        name: deviceAction['setting']['trn-name'],
                        type: deviceAction['setting']['setting-type']['trn-name'],
                        device: deviceAction['device-improx']['dev-name']
                    }
                })

                return resolve({ rows: deviceAction, pagination });
            })
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getDeviceActionsForAccessGroupsNamesWithPagination = async (listParameters?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const paginationString = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const settingFields = 'fields[setting]=trnName';
        const devActionFields = 'fields[devAction]=setting';
        const url = `devAction?include=setting&${settingFields}&${devActionFields}${paginationString}`
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);
        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }
            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const deviceAction: Name[] = data.map((deviceAction): Name => {
                    return {
                        value: deviceAction['id'],
                        label: deviceAction['setting']['trn-name'],
                    }
                })

                return resolve({ rows: deviceAction, pagination });
            })
        });
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}

export const storeAccessGroupAction = async (accessGroupAction, createAccessGroupAction = true): Promise<StoreEntityResult> => {
    try{
        const newRecord = {
            data: {
                type: "accessGroupAction",
                attributes: {
                    agaDirection: accessGroupAction.agaDirection
                },
                relationships: {
                    accessGroup: {
                        data: {
                            type: "accessGroup",
                            id: +accessGroupAction.accessGroupId,
                        }
                    },
                    devAction: {
                        data: {
                            type: "devAction",
                            id: +accessGroupAction.devActionId,
                        }
                    }
                }
            }
        }
        const url = `accessGroupAction`;
        let response: Response;

        await apiConnector.sendData(url, true, newRecord).then(data => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                return reject(processServerError(error));
            })
        });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (createAccessGroupAction) {
                    const content = await response.json();
                    if (content.errors) {
                        return reject({status: response.status, message: getConstraintViolation(content.errors[0].detail) })
                    }
                    new Deserializer().deserialize(content, (error) => {
                        if (error) {            
                            return reject({status: response.status, id: accessGroupAction.id, success: false, message: genericError });
                        }
                        return resolve({ id: accessGroupAction.id, success: true });
                    })

                    return;
                }
                if (response.status === NO_CONTENT) {
                    return resolve({ id: accessGroupAction.id, success: true })
                }

                const content = await response.json();
                return reject({status: response.status, id: accessGroupAction.id, success: false, message: content.errors.map(error => getConstraintViolation(error.detail)) });
            }
            return reject({status: INTERNAL_SERVER_ERROR, id: accessGroupAction.id, success: false, message: genericError });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const deleteAccessGroupAction = async (id: string): Promise<{ id: string, success: boolean }> => {
    try {
        const url = `accessGroupAction/${id}`;
        const door = {
            data: {
                type: "accessGroupAction",
                id: id
            }
        }
        const response = await apiConnector.deleteData(url, door)

        return new Promise(async (resolve, reject) => {
            if (response.status === NO_CONTENT) {
                return resolve({ id: id, success: true })
            }

            const json = await response.json();

            return reject({status: response.status, id: id, success: false, message: getConstraintViolation(json.errors[0].detail) });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}
